// arabicLeadership.ts
// Mission 112 - في قلب الحدث - القيادة الإنسانية
// Plateau TV arabe : débat sur le leadership humain à l'ère de l'IA

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
} from './storyLoader';

export interface ArabicLeadershipScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const arabicLeadershipCharacters: Character[] = [
  { id: 'leila',  name: 'ليلى الحسيني',   role: 'مقدمة البرنامج',    color: '#B45309', avatar: 'arabic_tv_leila_woman_30s' },
  { id: 'karim',  name: 'كريم المنصوري',  role: 'خبير إدارة الأعمال', color: '#1E3A5F', avatar: 'arabic_tv_karim_man_40s'   },
  { id: 'omar',   name: 'عمر البكري',      role: 'خبير التحول الرقمي', color: '#065F46', avatar: 'arabic_tv_omar_man_30s'    },
];

// ─── Scène 1 ──────────────────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'al-scene-1',
  title: 'الحلقة الأولى : مقدمة الضيف الخبير',
  duration: 4,
  content: {
    narrativeText:
      "استوديو «في قلب الحدث» — أضواء التصوير تنير منصة الحوار. ثلاثة كراسٍ متقابلة. ليلى الحسيني تجلس أمام الكاميرا بثقة هادئة.\n\n" +
      "**ليلى :** «أهلاً وسهلاً بكم في برنامج «في قلب الحدث». اليوم نناقش موضوعاً يشغل بال كل مدير وموظف في العالم العربي : هل يمكن للذكاء الاصطناعي أن يحلّ محل القائد الإنسان؟»\n\n" +
      "**ليلى :** «يسعدني استقبال ضيفنا الخبير في القيادة والإدارة. أهلاً بك على المنصة.»\n\n" +
      "**اللاعب :** «شكراً جزيلاً، سعيد بأن أكون معكم. هذا سؤال جوهري يستحق النقاش المعمّق.»\n\n" +
      "**كريم :** «أنا شخصياً أرى أن الخوارزميات يمكنها اتخاذ قرارات أسرع وأكثر موضوعية من أي مدير بشري.»\n\n" +
      "**عمر :** «لكن هل السرعة وحدها تصنع قائداً؟ القيادة تحتاج التعاطف، والحكمة، وفهم السياق الثقافي.»\n\n" +
      "**ليلى :** «ما رأيك أنت؟ ما الذي يجعل القيادة الإنسانية لا يمكن الاستغناء عنها؟»\n\n" +
      "**اللاعب :** «القيادة الحقيقية هي القدرة على إلهام الآخرين وبناء الثقة في أوقات الأزمات. لا يمكن لخوارزمية أن تُشعل الحماس في فريق يواجه تحدياً وجودياً.»\n\n" +
      "**كريم :** «إذن أنت تقول إن الذكاء الاصطناعي أداة وليس بديلاً؟»\n\n" +
      "**اللاعب :** «بالضبط. القائد الذكي يستخدم الذكاء الاصطناعي لتعزيز قراراته، لا للتنازل عن مسؤوليته.»",
    characters: ['leila', 'karim', 'omar'],
  } as StoryContent,
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const card1: SequenceItem = {
  type: 'card',
  id: 'al-card-1',
  title: 'أنماط القيادة الحديثة',
  duration: 5,
  content: {
    title: 'أنماط القيادة في عصر الذكاء الاصطناعي',
    body:
      "## أنماط القيادة الحديثة\n\n" +
      "### ١. القيادة التحويلية (Transformational Leadership)\n" +
      "- تركّز على إلهام الفريق وتحقيق رؤية مشتركة\n" +
      "- تُعزّز الإبداع والابتكار\n" +
      "- مثالية في بيئات التغيير التكنولوجي السريع\n\n" +
      "### ٢. القيادة بالخدمة (Servant Leadership)\n" +
      "- القائد في خدمة فريقه، لا العكس\n" +
      "- تبني الثقة والولاء على المدى البعيد\n" +
      "- قوية في الثقافات العربية التي تُقدّر الاحترام المتبادل\n\n" +
      "### ٣. القيادة الموقفية (Situational Leadership)\n" +
      "- تكييف الأسلوب القيادي حسب مستوى نضج الفريق\n" +
      "- تتطلب ذكاءً عاطفياً عالياً\n\n" +
      "### ٤. القيادة المبنية على البيانات (Data-Driven Leadership)\n" +
      "- استخدام الذكاء الاصطناعي لتحليل الأداء\n" +
      "- اتخاذ قرارات مستندة إلى الأدلة لا الحدس\n" +
      "- لكن القرار النهائي يبقى إنسانياً\n\n" +
      "### الذكاء العاطفي (EQ) مقابل الذكاء الاصطناعي (AI)\n" +
      "| المهارة | الذكاء الاصطناعي | القائد الإنسان |\n" +
      "|---|---|---|\n" +
      "| تحليل البيانات | ممتاز | جيد |\n" +
      "| التعاطف | لا يمتلكه | ضروري |\n" +
      "| بناء الثقة | محدود | أساسي |\n" +
      "| الإبداع الاستراتيجي | محدود | جوهري |",
  } as CardContent,
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

const chat1: SequenceItem = {
  type: 'chat',
  id: 'al-chat-1',
  title: 'أسئلة ليلى : القيادة والذكاء الاصطناعي',
  duration: 6,
  content: {
    tutorCharacterId: 'leila',
    messages: [
      {
        id: 'al-chat-q1',
        sender: 'character',
        characterId: 'leila',
        text: 'ما هي أهم مهارة يحتاجها القائد الإنسان في مواجهة الذكاء الاصطناعي؟',
        type: 'single-choice',
        options: [
          'القدرة على كتابة الكود البرمجي',
          'الذكاء العاطفي وبناء العلاقات الإنسانية',
          'إتقان أدوات إدارة المشاريع الرقمية',
          'السرعة في اتخاذ القرارات',
        ],
        correctAnswer: 'الذكاء العاطفي وبناء العلاقات الإنسانية',
        explanation: 'الذكاء العاطفي (EQ) هو الميزة التنافسية الأساسية للقائد الإنسان. الخوارزميات تتفوق في السرعة والتحليل، لكنها عاجزة عن التعاطف وبناء الثقة الحقيقية.',
      },
      {
        id: 'al-chat-q2',
        sender: 'character',
        characterId: 'leila',
        text: 'في نموذج القيادة التحويلية، ما الدور الذي يلعبه الذكاء الاصطناعي؟',
        type: 'single-choice',
        options: [
          'يحل محل القائد في صياغة الرؤية',
          'أداة تدعم القائد في تحليل الأداء وتخصيص الموارد',
          'يتولى إدارة الفريق بشكل مستقل',
          'يُقرر من يستحق الترقية',
        ],
        correctAnswer: 'أداة تدعم القائد في تحليل الأداء وتخصيص الموارد',
        explanation: 'في القيادة التحويلية، الذكاء الاصطناعي أداة تمكينية (enabling tool). يوفر البيانات والتحليلات، لكن رسالة الإلهام وبناء ثقافة الفريق تبقى حكراً على القائد الإنسان.',
      },
      {
        id: 'al-chat-q3',
        sender: 'character',
        characterId: 'leila',
        text: 'ما المقصود بـ «المدير الخوارزمي» (Algorithmic Manager) وما خطره؟',
        type: 'single-choice',
        options: [
          'مدير يستخدم الإكسل في عمله',
          'نظام ذكاء اصطناعي يتخذ قرارات الموارد البشرية آلياً دون تدخل إنساني',
          'مدير يعمل عن بعد عبر الأدوات الرقمية',
          'تطبيق لإدارة المهام اليومية',
        ],
        correctAnswer: 'نظام ذكاء اصطناعي يتخذ قرارات الموارد البشرية آلياً دون تدخل إنساني',
        explanation: 'المدير الخوارزمي خطر لأنه يُفقد العمال إنسانيتهم ويولّد قرارات متحيزة (biased). أبحاث منظمة العمل الدولية تؤكد أن الإشراف الإنساني ضروري في قرارات التوظيف والفصل.',
      },
    ],
  } as ChatContent,
};

// ─── Quiz ─────────────────────────────────────────────────────────────────────

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'al-quiz-1',
  title: 'اختبار : القيادة في عصر الذكاء الاصطناعي',
  duration: 5,
  content: {
    questions: [
      {
        id: 'al-q1-1',
        question: 'أي من الأساليب القيادية التالية يُعدّ الأنسب لإدارة فرق في بيئات التحول الرقمي؟',
        type: 'single-choice',
        options: [
          'القيادة الاستبدادية (Autocratic)',
          'القيادة التحويلية (Transformational)',
          'القيادة التساهلية (Laissez-faire)',
          'الإدارة بالأزمات',
        ],
        correctAnswer: 'القيادة التحويلية (Transformational)',
        explanation: 'القيادة التحويلية هي الأنسب للتحول الرقمي لأنها تُلهم الفرق للتكيف مع التغيير وتبني ثقافة مستعدة للابتكار.',
      },
      {
        id: 'al-q1-2',
        question: 'ما الفرق الجوهري بين «تفويض المهام» للذكاء الاصطناعي و«إسناد المسؤولية» إليه؟',
        type: 'single-choice',
        options: [
          'لا فرق، الكلمتان مترادفتان',
          'التفويض يعني إعطاء الأداة صلاحية التنفيذ، أما المسؤولية فتبقى دائماً بيد الإنسان',
          'الإسناد يعني إلغاء دور القائد بالكامل',
          'التفويض خاص بالمهام التقنية فقط',
        ],
        correctAnswer: 'التفويض يعني إعطاء الأداة صلاحية التنفيذ، أما المسؤولية فتبقى دائماً بيد الإنسان',
        explanation: 'هذا مبدأ جوهري في أخلاقيات الذكاء الاصطناعي: يمكنك تفويض التنفيذ للخوارزمية، لكن المساءلة والمسؤولية القانونية والأخلاقية تبقى دائماً على عاتق الإنسان.',
      },
      {
        id: 'al-q1-3',
        question: 'وفق مؤشر EQ (الذكاء العاطفي)، ما العنصر الأهم في بناء ثقة الفريق؟',
        type: 'single-choice',
        options: [
          'اتخاذ القرارات بسرعة',
          'التعاطف الحقيقي مع احتياجات الأفراد',
          'امتلاك أعلى المؤهلات الأكاديمية',
          'إنجاز المهام في الوقت المحدد',
        ],
        correctAnswer: 'التعاطف الحقيقي مع احتياجات الأفراد',
        explanation: 'التعاطف (Empathy) هو حجر الأساس في بناء الثقة. دراسات Google حول الفرق عالية الأداء (Project Aristotle) تؤكد أن الأمان النفسي — المبني على التعاطف — هو العامل الأول في نجاح الفرق.',
      },
    ],
  } as QuizContent,
};

// ─── Chapitre ─────────────────────────────────────────────────────────────────

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'القيادة في عصر الذكاء الاصطناعي',
  subtitle: 'Arabic — القيادة الإنسانية في مواجهة الأتمتة',
  learningObjectives: [
    'التمييز بين أنماط القيادة الحديثة',
    'فهم دور الذكاء العاطفي في القيادة',
    'تحليل إيجابيات وسلبيات المدير الخوارزمي',
    'بناء حجة مقنعة للقيادة الإنسانية بالعربية',
  ],
  sequence: [scene1, card1, chat1, quiz1],
};

export const arabicLeadershipScenario: ArabicLeadershipScenario = {
  id: 'arabic-leadership',
  title: 'في قلب الحدث - القيادة الإنسانية',
  subtitle: 'Leadership humain à l\'ère de l\'IA',
  certification: 'Arabic',
  characters: arabicLeadershipCharacters,
  chapters: [chapter1],
};

export default arabicLeadershipScenario;
